import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { DocsPage, CodeBlock } from './DocsComponents';

type MarkdownDocPageProps = {
  content: string;
};

const MarkdownDocPage: React.FC<MarkdownDocPageProps> = ({ content }) => {
  const location = useLocation();

  const resolveDocHref = (href?: string) => {
    if (!href) {
      return { href: '', isInternal: false };
    }

    if (href.startsWith('#')) {
      return { href, isInternal: false };
    }

    if (/^(https?:|mailto:|tel:)/i.test(href)) {
      return { href, isInternal: false };
    }

    const basePath = location.pathname.endsWith('/')
      ? location.pathname
      : `${location.pathname}/`;
    const baseUrl = typeof window !== 'undefined'
      ? `${window.location.origin}${basePath}`
      : `https://sentry.trading${basePath}`;

    let resolvedPathname = href;
    try {
      resolvedPathname = new URL(href, baseUrl).pathname;
    } catch {
      return { href, isInternal: false };
    }

    let normalizedPath = resolvedPathname.replace(/\.md$/i, '');
    if (normalizedPath.endsWith('/overview')) {
      normalizedPath = normalizedPath.replace(/\/overview$/, '');
    }

    if (normalizedPath.endsWith('/getting-started/introduction')) {
      normalizedPath = normalizedPath.replace(/\/getting-started\/introduction$/, '/getting-started');
    }

    if (normalizedPath.endsWith('/getting-started/quick-start')) {
      normalizedPath = normalizedPath.replace(/\/getting-started\/quick-start$/, '/quick-start');
    }

    return { href: normalizedPath, isInternal: true };
  };

  return (
    <DocsPage>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
          a: ({ href, children }) => {
            const resolved = resolveDocHref(href);
            if (!resolved.isInternal) {
              return (
                <a href={href} target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
                  {children}
                </a>
              );
            }
            return <Link to={resolved.href}>{children}</Link>;
          }
        }}
      >
        {content}
      </ReactMarkdown>
    </DocsPage>
  );
};

export default MarkdownDocPage;
